// aiDialerVoice.js — thin wrapper over twilioVoice.js for AI Dialer warm transfers.
//
// Why this exists: twilioVoice.js is the canonical PhonePanel softphone
// driver. We don't want to instantiate a second Twilio Device (it would
// double-register and double-ring). This module reuses the same Device and
// adds AI-Dialer-specific helpers:
//   - ensureDevice():        get-or-init the Device with a fresh voice-token
//   - joinAIDialerConference({callId, conferenceSid}):
//          place an outbound Twilio Voice connection whose TwiML is wired
//          server-side to drop the agent into the existing conference
//          (the AI screener has already moved the lead into it).
//
// The server-side TwiML App that backs the Voice SDK should look for
// params.purpose === 'ai-dialer-transfer' and route accordingly. If your
// TwiML App isn't wired for that yet, the call will simply fail and the
// caller can fall back to the cell-phone leg already handled by Day 3.

import { apiPost } from './api.js';
import * as voice from './twilioVoice.js';

let initPromise = null;

async function fetchToken() {
  const r = await apiPost('/phone/voice-token', {});
  const token = r?.data?.token || r?.token;
  if (!token) throw new Error('No voice token from server');
  return token;
}

export async function ensureDevice() {
  if (voice.isReady()) return voice.getDevice();
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const token = await fetchToken();
    const device = await voice.initVoice(token);
    initPromise = null;
    return device;
  })();
  return initPromise;
}

export async function joinAIDialerConference({ callId, conferenceSid }) {
  await ensureDevice();
  return voice.placeCall(undefined, {
    purpose: 'ai-dialer-transfer',
    callId: String(callId || ''),
    conferenceSid: String(conferenceSid || ''),
  });
}

export function mute(on = true)   { return voice.mute(!!on); }
export function hangup()           { return voice.hangUp(); }
export function getActiveCall()    { return voice.getActiveCall(); }
export function onVoiceEvent(...a) { return voice.on(...a); }
